import { Formik } from "formik";
import { useEffect, useState } from "react";
import { 
  Button, 
  Box, 
  Typography, 
  Radio, 
  RadioGroup, 
  FormControlLabel, 
  FormControl, 
  FormHelperText,
  Paper,
  Stack,
  Divider,
  Skeleton,
  Alert
} from "@mui/material";
import { CalendarMonth } from '@mui/icons-material';
import { trackAnalytics } from "src/features/analytics/analytics.slice";
import { getUserCalendar, updateUserCalendar } from "src/features/user/user.slice";
import { useAppDispatch, useAppSelector } from "src/store";
import { userCalendarSchema } from "src/utils/formSchemas";
import ErrorPopup from "../ErrorPopup";
import SuccessPopup from "../SuccessPopup";
import { delayTime } from "../ToastMessage";

const GoogleCalendarForm = () => {
  const [successMessage, setSuccessMessage] = useState("");
  const { userCalendar, userLoading, userError } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const calendarLength = userCalendar.length;
  const selected_calendar = userCalendar.find((elem) => {
    return elem.selected;
  });
  const initialValues = {calendar_id: selected_calendar?.id || ''};

  useEffect(() => {
    dispatch(getUserCalendar());
  }, []);

  const onSubmit = (data) => {
    dispatch(updateUserCalendar(data))
    .unwrap()
    .then((response) => {
      dispatch(trackAnalytics({
        action_name: "google_calendar_selected", 
        payload: { 
          description: "user updated google calendar form", 
          calendar_id: data.calendar_id
        }
      }));
      setSuccessMessage("Successfully Updated");
      setTimeout(() => {
        setSuccessMessage("");
      }, delayTime);
    });
  };

  return(
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <CalendarMonth sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Select Your Calendar
        </Typography>
      </Box>
      
      {userError && <ErrorPopup message={userError}/>}
      {successMessage && <SuccessPopup message={successMessage}/>}
      
      <Alert severity="info" sx={{ mb: 4 }}>
        We will use your Google Calendar to book open slots based on the booking availability.
        If you do not wish to be booked during one of those times,
        create a placeholder event on your calendar.
      </Alert>

      <Formik
        initialValues={initialValues}
        validationSchema={userCalendarSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          handleSubmit,
          handleChange,
          setFieldValue,
          resetForm,
          values,
          touched,
          errors,
        }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            {userCalendar.length === 0 && !userError && userLoading && (
              <Stack spacing={2}>
                <Skeleton variant="rectangular" height={60} />
                <Skeleton variant="rectangular" height={60} />
                <Skeleton variant="rectangular" height={60} />
              </Stack>
            )}

            <FormControl error={!!errors.calendar_id} sx={{ width: '100%' }}>
              <RadioGroup
                name="calendar_id"
                value={values.calendar_id}
                onChange={handleChange}
              >
                <Stack spacing={2}>
                  {userCalendar.map((item, i) => (
                    <Paper
                      key={item.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: values.calendar_id === item.id ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                        cursor: 'pointer',
                      }}
                    >
                      <FormControlLabel
                        value={item.id}
                        control={
                          <Radio 
                            disabled={userLoading}
                            sx={{ '&.Mui-checked': { color: 'primary.main' } }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarMonth sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body1" fontWeight={500}>
                              {item.summary}
                            </Typography>
                          </Box>
                        }
                        sx={{ m: 0, width: '100%' }}
                      />
                    </Paper>
                  ))}
                </Stack>
              </RadioGroup>
              {errors.calendar_id && touched.calendar_id && (
                <FormHelperText sx={{ mt: 2 }}>
                  {errors.calendar_id}
                </FormHelperText>
              )}
            </FormControl>

            <Divider sx={{ my: 4 }} />
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button 
                variant="contained" 
                type="submit" 
                disabled={userLoading}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Save
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => resetForm()}
                size="large"
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default GoogleCalendarForm;
